import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';

import withStyles from '@mui/styles/withStyles';
import Grid from '@mui/material/Grid';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';

// @mui/icon components
import PlusIcon from '@mui/icons-material//Add';
import DeleteIcon from '@mui/icons-material/Delete';
import UpArrowIcon from '@mui/icons-material/ArrowUpward';
import DownArrowIcon from '@mui/icons-material/ArrowDownward';
import LoginIcon from '@mui/icons-material/ExitToApp';

import { addPerson } from '../store/slices/personsSlice';
import {
  addUserToFirm,
  addAdminToFirm,
  removeUserFromFirm,
  promoteUser,
  demoteUser,
} from '../store/slices/firmInfoSlice';
import { setUserInfo } from '../store/slices/userInfoSlice';
import { setCaseIntendedClientId } from '../store/slices/intendedInfoSlice';
import { pushLogin } from '../store/slices/loginsSlice';
import { setApiResponseInfo } from '../store/slices/apiResponseInfoSlice';

import { createStdRequest, apiFetch } from '../utils/utils';

import DA from '../store/ReduxAccess';

import RegisterUserModal from './RegisterUserModal';
import ResetPasswordModal from './ResetPasswordModal';
import CrmButton from './CrmButton';
import KSpinner from './KSpinner';

const firmUserOverviewStyle = (theme) => ({
  root: {
    marginLeft: '20px',
    fontSize: '112%',
    '& .MuiSvgIcon-root': {
      fontSize: '1.3rem',
    },
  },
  pageTitle: {
    marginBottom: 0,
  },
  capabilitiesList: {
    marginTop: 0,
  },
  userTypeBlock: {
    marginTop: '16px',
  },
  personTable: {
    marginLeft: '16px',
    marginTop: '12px',
  },
  oddRow: {
    backgroundColor: '#fff',
  },
  evenRow: {
    backgroundColor: '#fff',
  },
  addButton: {
    marginLeft: '24px',
  },
  personName: {
    padding: '4px',
    fontWeight: 600,
  },
  td: {
    padding: '4px 12px',
  },
  leftIcon: {
    marginRight: '6px',
    transform: 'translate(0, 4px)',
  },
});

const ActionButtonTd = ({ classes, show, actionFunc, icon, label }) => {
  return show ? (
    <td className={classes.td}>
      <CrmButton filled size="small" onClick={actionFunc}>
        {icon}
        {label}
      </CrmButton>
    </td>
  ) : (
    <td />
  );
};

class PersonLine extends Component {
  render() {
    const { classes, i, id, name, email, lineType, viewerEmail, role, userDeletionFunc, parent } = this.props;
    const isLoggedInUser = email === viewerEmail;
    const isSuperAdmin = role === 'SuperAdmin';
    const isLocal = window.location.hostname === 'localhost';
    const showLoginButton =
      (lineType === 'admin' || lineType === 'user') && (isSuperAdmin || isLocal) && !isLoggedInUser;
    const showDeleteButton = lineType !== 'client' && (lineType === 'user' || !isLoggedInUser);
    const showFacePageButton = lineType === 'client';
    const showPromoteButton = lineType === 'user';
    const showDemoteButton = lineType === 'admin' && !isLoggedInUser;
    return (
      <tr className={i % 2 === 0 ? classes.evenRow : classes.oddRow}>
        <td className={classes.personName}>{name}</td>
        <ActionButtonTd
          classes={classes}
          show={showLoginButton}
          actionFunc={() => parent.logInToFirm(email)}
          icon={<LoginIcon className={classes.leftIcon} />}
          label="Log In"
        />
        <ActionButtonTd
          classes={classes}
          show={showDeleteButton}
          actionFunc={userDeletionFunc}
          icon={<DeleteIcon className={classes.leftIcon} />}
          label="Delete"
        />
        <ActionButtonTd
          classes={classes}
          show={showPromoteButton}
          actionFunc={() => parent.promoteToAdmin(id)}
          icon={<UpArrowIcon className={classes.leftIcon} />}
          label="Promote to Admin"
        />
        <ActionButtonTd
          classes={classes}
          show={showDemoteButton}
          actionFunc={() => parent.demoteToUser(id)}
          icon={<DownArrowIcon className={classes.leftIcon} />}
          label="Demote to Regular User"
        />
        <ActionButtonTd
          classes={classes}
          show={true}
          actionFunc={() => parent.showPasswordModal(id)}
          label="Reset Password"
        />
        {showFacePageButton ? (
          <td>
            <CrmButton filled onClick={() => parent.showCasePageForClient(id)}>
              Show Case Page
            </CrmButton>
          </td>
        ) : (
          <td />
        )}
      </tr>
    );
  }
}

class FirmUsersOverview extends Component {
  constructor(props) {
    super(props);

    this.state = {
      errorMessage: '',
      toHome: false,
      redirect: undefined,
      loading: false,
      showRegisterUserModal: false,
      showRegisterAdminModal: false,
      shouldShowPasswordModal: false,
      idOfUserWhosePasswordWeWantToReset: 0,
      shouldShowOwnershipInheritanceModal: false,
      personIdOfUserToRemove: 0,
    };
  }

  shouldShowKnownFirmsExportButton = false;

  useModalToRegisterUser = true;

  showRegisterUserForm() {
    this.setState({ showRegisterUserModal: true });
  }

  showRegisterAdminForm() {
    this.setState({ showRegisterAdminModal: true });
  }

  showPasswordModal = (userId) => {
    this.setState({ shouldShowPasswordModal: true, idOfUserWhosePasswordWeWantToReset: userId });
  };

  promoteToAdmin = (userId) => {
    this.switchRole(userId, 'User', 'Admin');
  };

  demoteToUser = (userId) => {
    this.switchRole(userId, 'Admin', 'User');
  };

  switchRole = (userId, oldRole, newRole) => {
    const uri = '/api/user/' + userId + '/switchRole';
    const bodyObj = { oldRole: oldRole, newRole: newRole };
    const request = createStdRequest(uri, 'PUT', bodyObj);
    const actionOnGoodResponse = () => {
      if (oldRole === 'User') {
        this.props.promoteUser(userId);
      } else {
        this.props.demoteUser(userId);
      }
    };
    apiFetch(this, request, actionOnGoodResponse);
  };

  showCasePageForClient = (personId) => {
    this.props.setCaseIntendedClientId(personId);
    this.setState({ redirect: '/case' });
  };

  offerToRemoveUserFromSystemAndChooseReplacement(userPersonId) {
    this.showOwnershipInheritanceModal(userPersonId);
  }

  showOwnershipInheritanceModal(userPersonId) {
    this.setState({
      shouldShowOwnershipInheritanceModal: true,
      personIdOfUserToRemove: userPersonId,
    });
  }

  getUserIdOfUserWithPersonId(personId) {
    const { adminsIdsNamesAndEmails, usersIdsNamesAndEmails } = this.props.firmInfo;
    const adminAndUserIdsNamesAndEmails = [...adminsIdsNamesAndEmails, ...usersIdsNamesAndEmails];
    const match = adminAndUserIdsNamesAndEmails.find((quad) => quad.personId === personId);
    return match ? match.id : '';
  }

  removeUserFromFirmWithInheritor = (personIdOfUserToRemove, personIdOfInheritor) => {
    const firmId = DA.getFirmId();
    const uri = `/api/firm/${firmId}/user/${personIdOfUserToRemove}/transferOwnership/${personIdOfInheritor}`;
    const request = createStdRequest(uri, 'DELETE');
    const actionOnGoodResponse = (dummy) => {
      const userId = this.getUserIdOfUserWithPersonId(personIdOfUserToRemove);
      this.props.removeUserFromFirm(userId);
      this.setState({ shouldShowOwnershipInheritanceModal: false });
    };
    apiFetch(this, request, actionOnGoodResponse);
  };

  deleteFirm = (firmId) => {
    this.setState({ loading: true });
    const uri = '/api/firm/' + firmId;
    const request = createStdRequest(uri, 'DELETE');
    apiFetch(this, request, (dummy) => this.setState({ redirect: '/firms' }));
  };

  logInToFirm(email) {
    console.log('Should now be logging in to provider: ' + this.props.firmInfo.firmName);

    const uri = '/api/Account/LogInToFirm?email=' + encodeURIComponent(email);
    const request = createStdRequest(uri);
    const actionOnGoodResponse = (json) => {
      const { success, userId, personId, email, phone, role, prefName, lastName, errorCode } = json;
      if (success) {
        const loginObj = { loginType: 'toFirm', email };
        this.props.pushLogin(loginObj);
        // redirect to Home
        this.setState({ toHome: true });
        // put the response's new user info into Redux state
        const userInfo = { userId, personId, email, phone, role, prefName, lastName };
        this.props.setUserInfo(userInfo);
      } else {
        // Possible problems, by error code:
        //   1) No such user name (email)
        //   2) Good user name, but not attached to a provider
        //   5) Firm has been deactivated
        switch (errorCode) {
          case 1:
            this.setState({ errorMessage: 'We have no account with that user name (email)' });
            break;
          case 2:
            this.setState({ errorMessage: 'The user is not associated with a provider' });
            break;
          case 5:
            this.setState({ errorMessage: 'The Account has been deactivated' });
            break;
          default:
            this.setState({ errorMessage: 'Unknown login error. Error code: ' + errorCode });
            break;
        }
      }
    };
    apiFetch(this, request, actionOnGoodResponse);
  }

  incorporateNewUser({ user }) {
    const prefNameToUse = user.preferredName || user.firstName;
    user.preferredFullName = prefNameToUse + ' ' + user.lastName;
    const idNameAndEmail = {
      id: user.appUserId,
      personId: user.personId,
      name: user.preferredFullName,
      email: user.email,
    };
    this.props.addUserToFirm(idNameAndEmail);
    this.props.addPerson(user);
    this.setState({ showRegisterUserModal: false });
  }

  incorporateNewAdmin({ user }) {
    const prefNameToUse = user.preferredName || user.firstName;
    user.preferredFullName = prefNameToUse + ' ' + user.lastName;
    const idNameAndEmail = {
      id: user.appUserId,
      personId: user.personId,
      name: user.preferredFullName,
      email: user.email,
    };
    this.props.addAdminToFirm(idNameAndEmail);
    this.props.addPerson(user);
    this.setState({ showRegisterAdminModal: false });
  }

  closeRegisterUserModal(arg) {
    this.setState({ showRegisterUserModal: false });
  }

  closeRegisterAdminModal(arg) {
    this.setState({ showRegisterAdminModal: false });
  }

  closePasswordModal(arg) {
    this.setState({ shouldShowPasswordModal: false });
  }

  downloadKnownFirms() {
    const knownFirms = DA.getAllKnownFirms();
    const obj = { knownFirms };
    const blob = new Blob([JSON.stringify(obj, null, 2)], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'knownFirms.json';
    a.click();
  }

  render() {
    if (this.state.redirect) {
      return <Redirect to={this.state.redirect} push />;
    }
    if (this.state.toHome) {
      return <Redirect to="/" />;
    }
    if (this.state.loading) {
      return <KSpinner />;
    }
    if (this.state.errorMessage) {
      return (
        <div>
          <h1 style={{ color: 'red' }}>Error Encountered</h1>
          <h3>{this.state.errorMessage}</h3>
        </div>
      );
    }
    const { classes, userInfo, firmInfo } = this.props;
    const { firmId, firmName, adminsIdsNamesAndEmails, usersIdsNamesAndEmails, clientsIdsAndNames } = firmInfo;
    const { /* email,*/ role } = userInfo;
    const permitted = role === 'SuperAdmin' || role === 'AllevaRep' || role === 'Admin';
    if (!permitted) {
      return (
        <div>
          <h2>Forbidden!!</h2>
          <h3>
            With role "<strong>{role}</strong>," you don't have permission to view this page
          </h3>
        </div>
      );
    }
    if (!firmId || firmId < 1) {
      return (
        <div>
          <h2>No firm selected to show.</h2>
          <div>ID of firm to show: {firmId}</div>
        </div>
      );
    }
    const isSuperAdmin = role === 'SuperAdmin';
    const viewerEmail = this.props.userInfo.email;

    const adminAndUserIdsNamesAndEmails = [...adminsIdsNamesAndEmails, ...usersIdsNamesAndEmails];
    const adminAndUserPersonIds = adminAndUserIdsNamesAndEmails.map((triple) => triple.personId);

    return (
      <div className={classes.root}>
        <OwnershipInheritanceModal
          show={this.state.shouldShowOwnershipInheritanceModal}
          personIdOfUserToRemove={this.state.personIdOfUserToRemove}
          personIdsOfAllUsers={adminAndUserPersonIds}
          okFunc={(disappearingUserPersonId, heirPersonId) =>
            this.removeUserFromFirmWithInheritor(disappearingUserPersonId, heirPersonId)
          }
          closeFunc={() => this.setState({ shouldShowOwnershipInheritanceModal: false })}
        />
        <RegisterUserModal
          page={this}
          forAdmin={false}
          show={this.state.showRegisterUserModal}
          firmId={DA.getFirmId()}
          onSuccess={(arg) => this.incorporateNewUser(arg)}
          onClose={(arg) => this.closeRegisterUserModal(arg)}
        />

        <RegisterUserModal
          page={this}
          forAdmin={true}
          show={this.state.showRegisterAdminModal}
          firmId={DA.getFirmId()}
          onSuccess={(arg) => this.incorporateNewAdmin(arg)}
          onClose={(arg) => this.closeRegisterAdminModal(arg)}
        />

        <ResetPasswordModal
          show={this.state.shouldShowPasswordModal}
          userId={this.state.idOfUserWhosePasswordWeWantToReset}
          onSuccess={() => this.closePasswordModal()}
          onCancel={() => this.closePasswordModal()}
        />

        <h3 className={classes.pageTitle}>Registered CRM Users at {firmName}</h3>
        {this.shouldShowKnownFirmsExportButton && (
          <div>
            <button onClick={() => this.downloadKnownFirms()}>Download Known Firms</button>
          </div>
        )}
        <div className={classes.userTypeBlock}>
          <strong>Admins:</strong>
          {/* <CrmButton onClick={() => this.acceptForm()}>Save</CrmButton> */}

          <CrmButton className={classes.addButton} filled size="small" onClick={() => this.showRegisterAdminForm()}>
            <PlusIcon className={classes.leftIcon} />
            Add Another Admin
          </CrmButton>
          <table className={classes.personTable}>
            <tbody>
              {adminsIdsNamesAndEmails.map((admin, i) => (
                <PersonLine
                  key={admin.id}
                  i={i}
                  classes={classes}
                  id={admin.id}
                  name={admin.name}
                  email={admin.email}
                  viewerEmail={viewerEmail}
                  lineType="admin"
                  role={role}
                  userDeletionFunc={() => this.offerToRemoveUserFromSystemAndChooseReplacement(admin.personId)}
                  parent={this}
                />
              ))}
            </tbody>
          </table>
        </div>
        <div className={classes.userTypeBlock}>
          <br />
          <strong>Users:</strong>
          <CrmButton className={classes.addButton} filled size="small" onClick={() => this.showRegisterUserForm()}>
            <PlusIcon className={classes.leftIcon} />
            Add User
          </CrmButton>
          <table className={classes.personTable}>
            <tbody>
              {usersIdsNamesAndEmails.map((user, i) => (
                <PersonLine
                  key={user.id}
                  i={i}
                  classes={classes}
                  id={user.id}
                  name={user.name}
                  email={user.email}
                  viewerEmail={viewerEmail}
                  lineType="user"
                  role={role}
                  userDeletionFunc={() => this.offerToRemoveUserFromSystemAndChooseReplacement(user.personId)}
                  parent={this}
                />
              ))}
            </tbody>
          </table>
        </div>

        {isSuperAdmin && (
          <div>
            <br />
            <strong>Clients &amp; Alumni:</strong>
            <table className={classes.personTable}>
              <tbody>
                {clientsIdsAndNames.map((pair, i) => (
                  <PersonLine
                    key={pair.id}
                    i={i}
                    classes={classes}
                    id={pair.id}
                    name={pair.name}
                    lineType="client"
                    role={role}
                    parent={this}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}

        {isSuperAdmin && (
          <div>
            <br />
            If you want to, you can {'  '}
            <CrmButton filled onClick={() => this.deleteFirm(firmId)}>
              Delete this provider now!
            </CrmButton>
          </div>
        )}
      </div>
    );
  }
}

/*
 * Call like
 * <OwnershipInheritanceModal
     show={T/F}
     personIdOfUserToRemove={3726}
     personIdsOfAllUsers={[<whatever>]}
     okFunc={}
     closeFunc={}
   />
 */
class OwnershipInheritanceModal extends Component {
  constructor(props) {
    super(props);

    this.state = {
      idOfUserToRemove: 0,
      idOfInheritor: 0,
      textToAskUser: '',
    };
  }

  componentDidUpdate(prevProps) {
    const { personIdOfUserToRemove, personIdsOfAllUsers } = this.props;
    if (personIdOfUserToRemove === prevProps.personIdOfUserToRemove) {
      return;
    }
    const userToRemove = DA.getPersonById(personIdOfUserToRemove);
    const nameOfUserToRemove = userToRemove.preferredFullName;
    const textToAskUser = `Who should "inherit" the ownership of Leads, Clients, and Contacts currently owned by ${nameOfUserToRemove}?`;

    const idsOfPossibleInheritors = personIdsOfAllUsers.filter((id) => id !== personIdOfUserToRemove);
    const namedDropdownOptions = idsOfPossibleInheritors.map((id) => {
      const name = DA.getPersonById(id).preferredFullName;
      const option = {
        name,
        personId: id,
      };
      return option;
    });
    const unnamedDropdownOption = {
      name: 'Unassigned',
      personId: 0,
    };
    this.dropdownOptions = [unnamedDropdownOption, ...namedDropdownOptions];
    this.setState({ textToAskUser, idOfUserToRemove: personIdOfUserToRemove });
  }

  onAutocompleteChange(personId) {
    this.setState({ idOfInheritor: personId });
  }

  render() {
    const { show, okFunc, closeFunc } = this.props;

    return (
      <Dialog open={show} onClose={closeFunc} fullWidth={true} maxWidth={'sm'}>
        <DialogTitle>Designate Heir</DialogTitle>

        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              {this.state.textToAskUser}
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                options={this.dropdownOptions}
                getOptionLabel={(option) => option.name}
                style={{ width: 220 }}
                renderInput={(params) => <TextField {...params} label="Inheritor" variant="outlined" />}
                value={this.dropdownOptions ? this.dropdownOptions[0] : null}
                onChange={(event, option) => this.onAutocompleteChange(option.personId)}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <CrmButton outlined onClick={closeFunc}>
            Cancel Deletion
          </CrmButton>
          <CrmButton filled onClick={() => okFunc(this.state.idOfUserToRemove, this.state.idOfInheritor)}>
            OK
          </CrmButton>
        </DialogActions>
      </Dialog>
    );
  }
}

const mapStateToProps = (state) => ({
  userInfo: state.userInfo,
  firmToShow: state.firms.idOfFirmToShow,
  firmInfo: state.firmInfo,
});
const actionProducers = {
  setUserInfo,
  promoteUser,
  demoteUser,
  removeUserFromFirm,
  setCaseIntendedClientId,
  pushLogin,
  setApiResponseInfo,
  addPerson,
  addUserToFirm,
  addAdminToFirm,
};

export default connect(mapStateToProps, actionProducers)(withStyles(firmUserOverviewStyle)(FirmUsersOverview));
